import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from "react-redux";
import { WorkPlannerContainer } from '@shared/ui/molecules/work-planner/WorkPlannerStructure';
import { WallPlannerDatesRow } from "./components/WallPlannerDatesRow";
import { WallPlannerTeamPlan } from "./components/WallPlannerTeamPlan";
import { WallPlannerWorkGroupPlan } from "./components/WallPlannerWorkGroupPlan";
import { WallPlannerUserRow } from "./components/WallPlannerUserRow";
import { WallPlannerHoverBox } from "./components/WallPlannerHoverBox";
import { loadFollowingGroups } from '@features/whereabouts/store/followingTeamsAndTags.duck';
import { selectIsBoxVisible } from "./data/WallPlannerHoverBox.duck";
import { WithWeeksInCurrentCalendarMonth } from '@features/whereabouts/hooks/WithWeekInCurrentCalendarMonth';
import { WithWorkPlannerUserActivities } from '@features/wall-planner/hooks/WithWorkPlannerUserActivities';
import { WithWorkPlannerTeamEvents } from '@features/wall-planner/hooks/WithWorkPlannerTeamEvents';
import { useWhereaboutsScope } from '@features/whereabouts/pages/whereabouts-v2/WhereaboutsScopeContext';
import { useScopedGroups } from '@features/whereabouts/hooks/useScopedGroups';

// Month grid for the groups in the enclosing whereabouts scope: the followed
// teams/workgroups by default, or the single pinned group on a scoped page.
export function MonthlyWhereaboutsView(props: Props) {
  const { scope, isScoped } = useWhereaboutsScope();
  const { teams, workGroups } = useScopedGroups(scope);
  const isHoverBoxVisible = useSelector(selectIsBoxVisible);
  const weeksInMonth = WithWeeksInCurrentCalendarMonth();
  const dispatch = useDispatch();

  WithWorkPlannerUserActivities({ teams, workGroups });
  WithWorkPlannerTeamEvents(teams);

  const workPlannerRef = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    if (isScoped) return;
    dispatch(loadFollowingGroups(false));
  }, [dispatch, isScoped]);

  useEffect(() => {
    const el = workPlannerRef.current;
    if (!el) return;
    const onScroll = () => setScrollLeft(el.scrollLeft);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <StickyDateRow id={'wallPlannerDateBar'}>
        <DateRowInner style={{ transform: `translateX(-${scrollLeft}px)` }}>
          <WallPlannerDatesRow weeksInMonth={weeksInMonth} hideDateFlicker={props.hideDateFlicker} />
        </DateRowInner>
      </StickyDateRow>
      <WorkPlannerContainer id={'workPlannerContainer'} ref={workPlannerRef}>
        {(teams || []).map((team, key) => (
          <WallPlannerTeamPlan key={key} team={team}
                               weeksInMonth={weeksInMonth}
                               isFirst={key === 0}/>
        ))}
        {(workGroups || []).map((tag, key) => (
          <WallPlannerWorkGroupPlan key={key} workGroup={tag}
                                    weeksInMonth={weeksInMonth}
                                    isFirst={key === 0}/>
        ))}

        <WallPlannerUserRow />
        <WallPlannerUserRow />
        <WallPlannerUserRow />
        <WallPlannerUserRow />

      </WorkPlannerContainer>
      {isHoverBoxVisible && <WallPlannerHoverBox/>}
    </>
  )
}

const StickyDateRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 1002;
  background: white;
  overflow: hidden;
`;

const DateRowInner = styled.div`
  width: max-content;
  will-change: transform;
`;

interface Props {
  hideDateFlicker?: boolean;
}
